/**
 * Centralized Activity Type System for TravelHearts
 *
 * This file provides a unified activity type system across all components,
 * including type definitions, display configurations, and utility functions.
 */

// Core activity type enum matching the ItinerariesSvg component
export enum ActivityType {
  HOTEL = 'hotel',
  RESTAURANT = 'restaurant',
  LANDMARK = 'landmark',
  MUSEUM = 'museum',
  AMUSEMENT = 'amusement',
  NIGHTLIFE = 'nightlife',
  EVENT = 'event',
  TRANSPORT = 'transport',
  SHOP = 'shop',
  OTHER = 'other'
}

// Additional activity types for broader compatibility
export enum ExtendedActivityType {
  // Core types (matching ActivityType)
  HOTEL = 'hotel',
  RESTAURANT = 'restaurant',
  LANDMARK = 'landmark',
  MUSEUM = 'museum',
  AMUSEMENT = 'amusement',
  NIGHTLIFE = 'nightlife',
  EVENT = 'event',
  TRANSPORT = 'transport',
  SHOP = 'shop',
  OTHER = 'other',

  // Extended types for API/Google Places compatibility
  ACCOMMODATION = 'accommodation',
  DINING_AND_DRINKS = 'dining and drinks',
  ACTIVITIES = 'activities',
  SHOPPING = 'shopping',
  SIGHTSEEING = 'sightseeing',
  CULTURE = 'culture',
  GOVERNMENT = 'government',
  CUSTOM = 'custom',
  DESTINATION = 'destination',
  SEARCH_RESULT = 'search_result',
  PARK = 'park'
}

// Type alias for backward compatibility with ItinerariesSvg
export type ItinerariesSvgType = ActivityType;

// Activity configuration interface
export interface ActivityConfig {
  type: ActivityType;
  displayName: string;
  category: string;
  color: string;
  symbol: string; // Visual symbol for map markers and UI
  description: string;
}

// Activity configurations with app's primary color scheme
export const ACTIVITY_CONFIGS: Record<ActivityType, ActivityConfig> = {
  [ActivityType.HOTEL]: {
    type: ActivityType.HOTEL,
    displayName: 'Hotels & Lodging',
    category: 'Accommodation',
    color: '#842234', // App primary color
    symbol: 'hotel',
    description: 'Hotels, resorts, and accommodation'
  },
  [ActivityType.RESTAURANT]: {
    type: ActivityType.RESTAURANT,
    displayName: 'Restaurants',
    category: 'Dining',
    color: '#842234',
    symbol: 'restaurant',
    description: 'Restaurants, cafes, and dining venues'
  },
  [ActivityType.LANDMARK]: {
    type: ActivityType.LANDMARK,
    displayName: 'Landmarks',
    category: 'Sightseeing',
    color: '#842234',
    symbol: 'landmark',
    description: 'Historic landmarks and monuments'
  },
  [ActivityType.MUSEUM]: {
    type: ActivityType.MUSEUM,
    displayName: 'Museums',
    category: 'Culture',
    color: '#842234',
    symbol: 'museum',
    description: 'Museums and cultural institutions'
  },
  [ActivityType.AMUSEMENT]: {
    type: ActivityType.AMUSEMENT,
    displayName: 'Amusement',
    category: 'Entertainment',
    color: '#842234',
    symbol: 'amusement',
    description: 'Amusement parks and entertainment venues'
  },
  [ActivityType.NIGHTLIFE]: {
    type: ActivityType.NIGHTLIFE,
    displayName: 'Nightlife',
    category: 'Entertainment',
    color: '#842234',
    symbol: 'nightlife',
    description: 'Bars, clubs, and nightlife venues'
  },
  [ActivityType.EVENT]: {
    type: ActivityType.EVENT,
    displayName: 'Events',
    category: 'Activities',
    color: '#842234',
    symbol: 'event',
    description: 'Events, shows, and scheduled activities'
  },
  [ActivityType.TRANSPORT]: {
    type: ActivityType.TRANSPORT,
    displayName: 'Transportation',
    category: 'Transport',
    color: '#842234',
    symbol: 'transport',
    description: 'Transportation and transit'
  },
  [ActivityType.SHOP]: {
    type: ActivityType.SHOP,
    displayName: 'Shopping',
    category: 'Shopping',
    color: '#842234',
    symbol: 'shop',
    description: 'Shops, markets, and retail'
  },
  [ActivityType.OTHER]: {
    type: ActivityType.OTHER,
    displayName: 'Other',
    category: 'Miscellaneous',
    color: '#842234',
    symbol: 'other',
    description: 'Other activities and places'
  }
};

// Utility class for activity type operations
export class ActivityTypeHelper {
  /**
   * Get activity configuration by type
   */
  static getConfig(type: string): ActivityConfig {
    const activityType = this.normalizeType(type);
    return (
      ACTIVITY_CONFIGS[activityType] || ACTIVITY_CONFIGS[ActivityType.OTHER]
    );
  }

  /**
   * Get display name for activity type
   */
  static getDisplayName(type: string): string {
    return this.getConfig(type).displayName;
  }

  /**
   * Get color for activity type
   */
  static getColor(type: string): string {
    return this.getConfig(type).color;
  }

  /**
   * Get visual symbol for activity type (used for map markers and UI)
   */
  static getSymbol(type: string): string {
    return this.getConfig(type).symbol;
  }

  /**
   * Get category for activity type
   */
  static getCategory(type: string): string {
    return this.getConfig(type).category;
  }

  /**
   * Normalize any activity type string to core ActivityType
   */
  static normalizeType(type?: string): ActivityType {
    if (!type) return ActivityType.OTHER;

    const lowerType = type.toLowerCase().replace(/[-_\s]/g, '');

    // Direct matches
    if (Object.values(ActivityType).includes(type as ActivityType)) {
      return type as ActivityType;
    }

    // Extended type mappings
    switch (lowerType) {
      case 'accommodation':
      case 'lodging':
      case 'campsite':
        return ActivityType.HOTEL;

      case 'food':
      case 'diningandrinks':
      case 'dining':
      case 'drinks':
      case 'coffee':
      case 'bar':
      case 'cafe':
        return ActivityType.RESTAURANT;

      case 'park':
      case 'sightseeing':
      case 'tourist':
      case 'attraction':
        return ActivityType.LANDMARK;

      case 'culture':
      case 'gallery':
      case 'exhibition':
        return ActivityType.MUSEUM;

      case 'entertainment':
      case 'fun':
      case 'recreation':
        return ActivityType.AMUSEMENT;

      case 'night':
      case 'club':
      case 'pub':
        return ActivityType.NIGHTLIFE;

      case 'activities':
      case 'activity':
      case 'show':
      case 'performance':
        return ActivityType.EVENT;

      case 'transportation':
      case 'transit':
      case 'travel':
      case 'car':
      case 'bus':
      case 'train':
        return ActivityType.TRANSPORT;

      case 'shopping':
      case 'store':
      case 'market':
      case 'retail':
        return ActivityType.SHOP;

      case 'government':
      case 'custom':
      case 'destination':
      case 'searchresult':
      case 'search_result':
      default:
        return ActivityType.OTHER;
    }
  }

  /**
   * Get all available activity types
   */
  static getAllTypes(): ActivityType[] {
    return Object.values(ActivityType);
  }

  /**
   * Get all activity configurations
   */
  static getAllConfigs(): ActivityConfig[] {
    return Object.values(ACTIVITY_CONFIGS);
  }

  /**
   * Get activities grouped by category
   */
  static getByCategory(): Record<string, ActivityConfig[]> {
    const grouped: Record<string, ActivityConfig[]> = {};

    Object.values(ACTIVITY_CONFIGS).forEach((config) => {
      if (!grouped[config.category]) {
        grouped[config.category] = [];
      }
      grouped[config.category].push(config);
    });

    return grouped;
  }

  /**
   * Check if a type is valid
   */
  static isValidType(type: string): boolean {
    return (
      Object.values(ActivityType).includes(type as ActivityType) ||
      Object.values(ExtendedActivityType).includes(type as ExtendedActivityType)
    );
  }
}

// Default export
export default ActivityTypeHelper;
